/**
 * `useMediaUpload` — the reusable entry point for uploading files to the
 * media-v3 service from anywhere in the webapp (order attachments, place media,
 * …). It runs the init → PUT parts → final pipeline per file and exposes the
 * resulting jobs plus in-session resumable uploads.
 *
 * The jobs themselves live in the module-level upload store
 * (`_lib/upload-store.ts`), under a SCOPE:
 *
 * - A NAMED scope (`useMediaUpload({ scope: draftKey })`) survives the hook: a
 *   composer that is unmounted by a tab switch while its files are still going
 *   up finds them — in flight or done — when it mounts again with the same name.
 * - An EPHEMERAL scope (no `scope` given) is released on unmount: the uploads
 *   keep going so their bytes land and get processed, and are dropped once
 *   nothing more can happen to them.
 *
 * Processing status is REALTIME, not polled, and it is watched by the root
 * `MediaUploadTracker` for EVERY entry in the store — over the ONE SSE
 * connection owned by `RealtimeRelayProvider` — so a started upload is never
 * forgotten because its starter went away. The box fleet is entirely the media
 * service's concern — nothing here knows pools or boxes exist; callers just
 * upload and read status.
 */

import { useAuth } from "@clerk/clerk-react";
import { useCallback, useEffect, useId, useMemo, useState } from "react";

import { env } from "@/env";
import { computePartCount } from "../_api/media-v3-client";
import {
  findResumeRecord,
  fingerprintFile,
  loadResumeRecords,
  type ResumeRecord,
} from "../_lib/resume-store";
import {
  applyUploadEvent,
  runUpload,
  type UploadDeps,
} from "../_lib/upload-pipeline";
import { entriesInScope, useUploadStore } from "../_lib/upload-store";
import type {
  MediaUploadAccess,
  MediaUploadEntry,
  MediaUploadJob,
  MediaUploadOptions,
} from "../_types/media-upload";

export type { MediaUploadOptions };

/** Clerk JWT template the media service validates against. */
export const CLERK_TOKEN_TEMPLATE = "birdwatch-template";
/**
 * How long a DONE job keeps waiting for its AI text after `attachment.processed`
 * before the analysis is settled as `none`. Media announces the text in the SAME
 * notify step, right behind `processed` (`aiAnalyzed: true` is the promise), so the
 * gap is normally milliseconds — this is a backstop for a lost relay event, not a
 * processing budget. A consumer gating on "analysis settled" (the Birdie composer's
 * send button) must never hang forever on it; a late `transcript.ready` still folds
 * in, since `ready` always wins in the reducer.
 */
export const ANALYSIS_WAIT_MS = 60_000;

export interface UseMediaUploadConfig {
  /**
   * Name the uploads so another mount with the same name picks them up (a
   * composer's draft key). Omit for uploads that belong to this mount only.
   */
  scope?: string;
}

export interface UseMediaUploadResult {
  jobs: MediaUploadJob[];
  /** The jobs with what the browser knows about them (uid, the File, when picked). */
  entries: MediaUploadEntry[];
  /**
   * Start uploading the given files (auto-resumes any that match a fresh record).
   * Returns the minted job ids IN FILE ORDER, so a caller can bind its own state to
   * each job by identity — never by name/size, which processing rewrites.
   */
  upload: (files: File[], opts?: MediaUploadOptions) => string[];
  /**
   * Abort one job's upload: in-flight part PUTs stop, its resume record is dropped,
   * and the job ends `failed` ("Upload cancelled"). A no-op once the bytes are on the
   * server — processing cannot be recalled from here.
   */
  cancel: (jobId: string) => void;
  /** Drop one job from the scope (cancel it first if it is still uploading). */
  remove: (jobId: string) => void;
  /** Re-run a FAILED job from the bytes the store still holds; same id, same uid. */
  retry: (jobId: string) => void;
  /** Clear the scope's job list (does not cancel in-flight uploads). */
  reset: () => void;
  /** Fresh interrupted uploads that can be resumed by re-picking the file. */
  resumeRecords: ResumeRecord[];
  /** Resume the interrupted upload matching a re-picked file (else starts fresh). */
  resumeFile: (file: File) => void;
}

function genJobId(file: File): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${file.name}`;
}

/** The route pair an upload call uses — `authed` unless the caller asked for `public`. */
function accessOf(options: MediaUploadOptions | undefined): MediaUploadAccess {
  return options?.access ?? "authed";
}

/** A fresh job for `file` — with `id` when a retry keeps its identity. */
function createJob(
  file: File,
  access: MediaUploadAccess,
  id = genJobId(file),
): MediaUploadJob {
  return {
    id,
    fileName: file.name,
    size: file.size,
    contentType: file.type,
    parts: computePartCount(file.size),
    partsDone: 0,
    bytesSent: 0,
    phase: "queued",
    status: null,
    access,
    attachmentId: null,
    contentUri: null,
    thumbnailUri: null,
    aiAnalyzed: false,
    analysis: { status: "pending" },
    draftStored: null,
    retries: 0,
    resumedSkipped: 0,
    error: null,
    note: null,
  };
}

/**
 * The picker's own identity for a file, when it gave one. rc-upload stamps a
 * `uid` on every File it hands over, and the form rows are keyed by it.
 */
function pickUid(file: File, fallback: string): string {
  const uid = (file as File & { uid?: unknown }).uid;
  return typeof uid === "string" && uid ? uid : fallback;
}

/** One controller per job still uploading — `cancel(jobId)` aborts through it. */
const controllers = new Map<string, AbortController>();

/**
 * Run the pipeline for one entry, folding its events into the store. Detached
 * from React on purpose: the upload outlives whichever component started it.
 */
function startEntryUpload(
  entry: MediaUploadEntry,
  deps: UploadDeps,
  resumeRecord: ResumeRecord | null,
): void {
  const jobId = entry.job.id;
  const controller = new AbortController();
  controllers.set(jobId, controller);
  void runUpload({
    file: entry.file,
    deps,
    resumeRecord,
    signal: controller.signal,
    transcriptDraft: entry.options?.transcriptDraft,
    access: accessOf(entry.options),
    runAiAnalysis: entry.options?.runAiAnalysis,
    onEvent: (event) => {
      // Fold into the STORE's job, never a copy held here: the tracker writes
      // the same job, and a plain file's `attachment.processed` can land before
      // this run's `/final` response does.
      useUploadStore
        .getState()
        .patchJob(jobId, (job) => applyUploadEvent(job, event));
      if (event.type !== "final") return;
      const landed = useUploadStore
        .getState()
        .entries.find((candidate) => candidate.job.id === jobId)?.job;
      if (landed) entry.options?.onLanded?.(landed);
    },
  }).finally(() => controllers.delete(jobId));
}

export function useMediaUpload(
  config: UseMediaUploadConfig = {},
): UseMediaUploadResult {
  const { getToken } = useAuth();
  const mountId = useId();
  const named = config.scope;
  const scope = named ?? `ephemeral:${mountId}`;
  const allEntries = useUploadStore((state) => state.entries);
  const entries = useMemo(
    () => entriesInScope(allEntries, scope),
    [allEntries, scope],
  );
  const jobs = useMemo(() => entries.map((entry) => entry.job), [entries]);
  const [resumeRecords, setResumeRecords] = useState<ResumeRecord[]>([]);

  const refreshResumeRecords = useCallback(() => {
    setResumeRecords(loadResumeRecords());
  }, []);

  // localStorage is client-only — hydrate resume records after mount.
  useEffect(() => {
    refreshResumeRecords();
  }, [refreshResumeRecords]);

  // An ephemeral scope belongs to this mount alone: let its uploads finish in
  // the background, but nobody will ever read them again.
  useEffect(() => {
    if (named) return;
    return () => {
      useUploadStore.getState().releaseScope(scope);
    };
  }, [named, scope]);

  const deps = useMemo<UploadDeps>(
    () => ({
      baseUrl: env.PUBLIC_MEDIA_SERVICE_URL,
      getToken: () => getToken({ template: CLERK_TOKEN_TEMPLATE }),
      onStoreChange: refreshResumeRecords,
    }),
    [getToken, refreshResumeRecords],
  );

  const upload = useCallback(
    (files: File[], opts?: MediaUploadOptions): string[] => {
      if (files.length === 0) return [];
      const createdAt = new Date().toISOString();
      const access = accessOf(opts);
      const started = files.map((file) => {
        const job = createJob(file, access);
        const entry: MediaUploadEntry = {
          job,
          scope,
          uid: pickUid(file, job.id),
          file,
          createdAt,
          options: opts,
          orphaned: false,
        };
        return {
          entry,
          record: findResumeRecord(fingerprintFile(file, access)),
        };
      });
      useUploadStore.getState().add(started.map((s) => s.entry));
      for (const { entry, record } of started) {
        startEntryUpload(entry, deps, record);
      }
      return started.map((s) => s.entry.job.id);
    },
    [scope, deps],
  );

  const cancel = useCallback((jobId: string) => {
    controllers.get(jobId)?.abort();
  }, []);

  const remove = useCallback(
    (jobId: string) => {
      cancel(jobId);
      useUploadStore.getState().remove([jobId]);
    },
    [cancel],
  );

  const retry = useCallback(
    (jobId: string) => {
      const store = useUploadStore.getState();
      const entry = store.entries.find(
        (candidate) => candidate.job.id === jobId,
      );
      if (entry?.job.phase !== "failed") return;
      const access = accessOf(entry.options);
      const fresh: MediaUploadEntry = {
        ...entry,
        job: createJob(entry.file, access, entry.job.id),
      };
      store.patchJob(jobId, () => fresh.job);
      startEntryUpload(
        fresh,
        deps,
        findResumeRecord(fingerprintFile(entry.file, access)),
      );
    },
    [deps],
  );

  const reset = useCallback(() => {
    useUploadStore.getState().clearScope(scope);
  }, [scope]);

  const resumeFile = useCallback(
    (file: File) => {
      upload([file]);
    },
    [upload],
  );

  return {
    jobs,
    entries,
    upload,
    cancel,
    remove,
    retry,
    reset,
    resumeRecords,
    resumeFile,
  };
}
